import { BooleanInput } from '@angular/cdk/coercion';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlContainer, FormBuilder, FormControl, FormGroup, FormGroupDirective } from '@angular/forms';
import { MatSelect } from '@angular/material/select';

import { CommonFunctionService } from '../../../core/services/common-function/common-function.service';

import { debounceTime, distinctUntilChanged, skip, startWith, Subject, takeUntil } from 'rxjs';

interface DropDownEvent {
  search: string;
  selections: any;
  selectedOptionValue: any;
}

@Component({
  selector: 'dropdown-multiselect',
  templateUrl: './dropdown-multiselect.component.html',
  styleUrls: ['./dropdown-multiselect.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      deps: [[Optional, FormGroupDirective]],
      useFactory: (formDirective: FormGroupDirective) => formDirective,
    },
  ],
})
export class DropdownMultiselectComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('dropdown') dropdown!: MatSelect;

  @Input() autoClose: boolean = true;
  @Input() control!: any;
  @Input() controlName!: string;
  @Input() customClasses!: string;
  @Input() customOptions: boolean = false;
  @Input() defaultValue: any;
  @Input() disabled: boolean = false;
  @Input() displayFormat!: string;
  @Input() hint!: string;
  @Input() hintType!: 'error' | 'warning' | 'success';
  @Input() isShowHint: boolean = false;
  @Input() modelValue!: any;
  @Input() multiple: BooleanInput = false;
  @Input() options: any[] = [];
  @Input() placeholder!: string;
  @Input() searchable: boolean = true;
  @Input() selections!: any;
  @Input() showClear: boolean = true;
  @Input() showCustomRequiredMark: boolean = false;
  @Input() tabindex!: number;
  @Input() valueField!: string;
  @Input() showLoading: boolean = false;
  @Input() dataLoaded: boolean = false;
  @Input() option!: string;
  @Input() isTextCamelCase: boolean = false;
  @Input() optionDisabled: boolean = false;

  @Output() dropDownEvent = new EventEmitter<DropDownEvent>();
  @Output() modelValueChange = new EventEmitter();

  dropDownForm: FormGroup;
  noMatchingOption: boolean = false;
  selectControl: FormControl = new FormControl();
  selectedOptions!: any[];
  optionsSearched: any[] = [];
  prevSelectedValue: any;
  oldOptionValues: any = [];
  isInitialValue: boolean = true;

  private clonedOptions!: any[];
  private destroy$: Subject<void>;

  constructor(
    @Optional() private controlContainer: ControlContainer,
    private formBuilder: FormBuilder,
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private chagegeDetector: ChangeDetectorRef,
    private commonFunctionService: CommonFunctionService
  ) {
    this.dropDownForm = this.createDropdownForm();
    this.destroy$ = new Subject<void>();
  }

  ngOnInit(): void {
    this.setEventListeners();
    if (this.tabindex) {
      setTimeout(() => {
        this.renderer.setAttribute(
          this.elementRef.nativeElement.querySelector('mat-select'),
          'tabindex',
          this.tabindex?.toString()
        );
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Options
    if (changes['options']?.currentValue != null) {
      if (this.valueField && this.valueField !== '') {
        this.options = this.options.filter(option => option[this.valueField] && option[this.valueField] != null);
        this.optionsSearched = this.options.filter(
          option => option[this.valueField] && option[this.valueField] != null
        );
      } else {
        this.options = this.options.filter(option => option && option != null);
        this.optionsSearched = this.options.filter(option => option && option != null);
      }
      let selectedList =
        this.clonedOptions && this.selectControl.value
          ? this.clonedOptions.filter(option =>
              this.multiple
                ? this.valueField && this.valueField !== ''
                  ? this.selectControl.value.includes(option[this.valueField])
                  : this.isObjectInData(this.selectControl.value, option)
                : this.valueField && this.valueField !== ''
                ? this.selectControl.value === option[this.valueField]
                : this.commonFunctionService.deepEqual(this.selectControl.value, option)
            )
          : [];

      // Create a set of ids from the data array for efficient lookup
      if (!this.valueField && this.valueField === '') {
        for (const element of selectedList) {
          let dataElement = this.removeObjectKey(element, ['guestDisplayName', 'isAdded']);
          let index = this.options.findIndex(item => this.commonFunctionService.deepEqual(item, dataElement));

          if (index !== -1) {
            this.options.splice(index, 1);
          }
        }
      }

      let dataIds: any =
        this.valueField && this.valueField !== ''
          ? new Set(this.options.map(item => item[this.valueField]))
          : this.options.map(item => item);

      // Filter the bbb array to remove objects with ids present in the data array
      let removedSelectionOption =
        this.valueField && this.valueField !== ''
          ? selectedList.filter(item => !dataIds.has(item[this.valueField]))
          : selectedList.filter(item => !this.checkForTargetObject(dataIds, item));

      this.options = [...this.options, ...removedSelectionOption];
      this.clonedOptions = [...this.options];
      if (this.options.length > 0) {
        this.formatDisplayName();
      }

      let searchQuery = this.dropDownForm?.get(['search'])?.value;
      if (searchQuery) {
        this.optionsSearched =
          searchQuery?.length > 0
            ? this.clonedOptions
              ? this.clonedOptions.filter(option =>
                  option[this.getDisplayFieldName()]
                    ?.toString()
                    .toLowerCase()
                    .replace(/\s/g, '')
                    .includes(searchQuery.toLowerCase().replace(/\s/g, ''))
                )
              : []
            : this.clonedOptions
            ? this.clonedOptions
            : [];

        this.options =
          searchQuery?.length > 0
            ? this.clonedOptions
              ? this.clonedOptions.filter(
                  option =>
                    option[this.getDisplayFieldName()]
                      ?.toString()
                      .toLowerCase()
                      .replace(/\s/g, '')
                      .includes(searchQuery.toLowerCase().replace(/\s/g, '')) ||
                    (this.multiple
                      ? this.valueField && this.valueField !== ''
                        ? this.selectControl?.value?.includes(option[this.valueField])
                        : this.isObjectInData(this.selectControl?.value, option)
                      : this.valueField && this.valueField !== ''
                      ? this.selectControl?.value === option[this.valueField]
                      : this.commonFunctionService.deepEqual(this.selectControl?.value, option))
                )
              : []
            : this.clonedOptions
            ? this.clonedOptions
            : [];
        this.noMatchingOption = this.options.length === 0;
      }
      if (this.isInitialValue && this.options.length > 0) {
        this.oldOptionValues = this.options;
        this.isInitialValue = false;
      }
      this.chagegeDetector.detectChanges();
    }

    // Control name / Control
    if (changes['controlName']?.currentValue != null || changes['control']?.currentValue != null) {
      this.createFormControl();
    } else {
      this.selectedOptions = [];
    }

    // Prepopulate the control display
    if (this.selectControl?.value !== null && this.selectControl?.value !== undefined) {
      this.selectedOptions = this.onSelectSetValue(this.selectControl.value);
    }

    // Model Value - TDF
    if (changes['modelValue']?.currentValue != null) {
      this.selectControl?.setValue(this.modelValue);
    }

    if (changes['disabled']?.currentValue != null && changes['disabled']?.currentValue) {
      this.selectControl?.disable();
    } else {
      this.selectControl?.enable();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** To Format Control Name */
  getDisplayFieldName() {
    const controlName = this.controlName ? this.controlName : '';

    return controlName + 'DisplayName';
  }

  getName(): string {
    if (
      this.selectControl?.value === null ||
      this.selectControl?.value === undefined ||
      this.selectControl?.value === ''
    ) {
      return '';
    }

    if (!this.valueField) {
      return this.selectControl.value[this.getDisplayFieldName()];
    }

    const selectedOption = this.options?.find((option: any) => option[this.valueField] === this.selectControl.value);

    return selectedOption && selectedOption[this.getDisplayFieldName()];
  }

  onClickAddItem(option: any) {
    option.isAdded = true;
    let selectedValues = this.selectControl.value ? this.selectControl.value : [];
    if (this.multiple) {
      selectedValues.push(this.valueField && this.valueField !== '' ? option[this.valueField] : option);
      this.selectControl.setValue(selectedValues);
    } else {
      this.selectControl.setValue(option);
    }

    // To Publish Dropdown state value
    this.publishDropDownState();

    if (this.customOptions) {
      //Clear Selection
      this.dropdown.value = null;
    }
  }

  onClickClear(): void {
    this.dropDownForm.reset();
  }

  onClickClearSelection(): void {
    this.dropdown.value = null;
    this.publishDropDownState();
  }

  onClickOptions(ev: Event): void {
    if (!this.autoClose) {
      this.dropdown.open();
    }
    this.dropDownForm.get(['search'])?.reset();

    this.prevSelectedValue = this.selectedOptions;

    this.clonedOptions = [...this.clonedOptions, ...this.oldOptionValues];
    this.clonedOptions = [...new Set(this.clonedOptions)];

    ev.stopPropagation();
  }

  onOptionRemoved(removedOption: any): void {
    const index = this.selectedOptions.indexOf(removedOption);
    if (index > -1) {
      this.selectedOptions.splice(index, 1);
    }
    const updatedOptions = this.selectedOptions.map(option =>
      this.valueField && this.valueField !== '' ? option[this.valueField] : option
    );
    this.selectControl.setValue(updatedOptions);
    this.publishDropDownState();
  }

  onSelectChange(selectedValue: any): void {
    this.selectedOptions = this.onSelectSetValue(selectedValue.value);

    if (this.autoClose) {
      this.dropdown.close();
    }

    // To Publish Dropdown state value
    this.publishDropDownState();
  }

  /**
   * To Emit the Search string and Dropdown selection to Parent
   */
  publishDropDownState() {
    const searchQuery = this.dropDownForm?.get(['search'])?.value ?? '';
    // if (!this.selectControl.value && !searchQuery) {
    //   return;
    // }
    this.dropDownEvent.next({
      search: searchQuery,
      selections: this.selectControl.value,
      selectedOptionValue: this.onSelectSetValue(this.selectControl.value) ?? [],
    });

    // Publish Control Value to  Parent  - TDF
    if (this.modelValue) {
      this.modelValue = this.selectControl.value;
      this.modelValueChange.emit(this.selectControl.value);
    }
  }

  private createDropdownForm(): FormGroup {
    return this.formBuilder.group({
      search: [''],
    });
  }

  private createFormControl(): void {
    const selectForm = this.controlContainer?.control?.get(this.controlName) as FormControl;
    this.selectControl = selectForm ?? this.control;
  }

  private formatDisplayName(): void {
    this.options?.forEach((option: any) => {
      let displayName = this.displayFormat;
      Object.keys(option).forEach(key => {
        if (this.displayFormat.includes(key)) {
          displayName = displayName.replace(key, option[key] ?? '');
        }
        if (option[key] === 'ALL_CENTER') {
          displayName = displayName.split('(').at(0)?.toString()!;
        }
      });

      displayName = displayName.replace('--', '-');
      displayName = displayName.replace('()', '');

      option[this.getDisplayFieldName()] = displayName;
      option.isAdded = false;
    });
  }

  private onSelectSetValue(selectedValues: any): any[] {
    if (Array.isArray(selectedValues)) {
      // If selected values is a list

      if (this.valueField === '') {
        return this.clonedOptions?.filter(option => !!selectedValues?.find((value: any) => option === value));
      }

      return this.clonedOptions?.filter(
        option => !!selectedValues?.find((value: any) => option[this.valueField] === value)
      );
    } else if (typeof selectedValues === 'object') {
      // If selected values is a object
      if (this.valueField === '') {
        return this.clonedOptions?.filter(option => option === selectedValues);
      }

      return this.clonedOptions?.filter(option => option[this.valueField] === selectedValues);
    } else {
      return this.clonedOptions?.filter(option => selectedValues?.toString() == option[this.valueField]);
    }
  }

  private setEventListeners(): void {
    // Search value changes
    this.dropDownForm
      ?.get(['search'])
      ?.valueChanges.pipe(startWith(''), debounceTime(500), distinctUntilChanged(), skip(1), takeUntil(this.destroy$))
      .subscribe(searchQuery => {
        this.optionsSearched =
          searchQuery?.length > 0
            ? this.clonedOptions
              ? this.clonedOptions.filter(option =>
                  option[this.getDisplayFieldName()]
                    ?.toString()
                    .toLowerCase()
                    .replace(/\s/g, '')
                    .includes(searchQuery.toLowerCase().replace(/\s/g, ''))
                )
              : []
            : this.clonedOptions
            ? this.clonedOptions
            : [];

        this.options =
          searchQuery?.length > 0
            ? this.clonedOptions
              ? this.clonedOptions.filter(
                  option =>
                    option[this.getDisplayFieldName()]
                      ?.toString()
                      .toLowerCase()
                      .replace(/\s/g, '')
                      .includes(searchQuery.toLowerCase().replace(/\s/g, '')) ||
                    (this.multiple
                      ? this.valueField && this.valueField !== ''
                        ? this.selectControl?.value?.includes(option[this.valueField])
                        : this.isObjectInData(this.selectControl?.value, option)
                      : this.valueField && this.valueField !== ''
                      ? this.selectControl?.value === option[this.valueField]
                      : this.commonFunctionService.deepEqual(this.selectControl?.value, option))
                )
              : []
            : this.clonedOptions
            ? this.clonedOptions
            : [];
        this.noMatchingOption = this.options.length === 0;

        // To Publish Dropdown state value
        if (this.selectControl.value && !this.dropDownForm.get(['search'])?.value) {
          return;
        }
        this.publishDropDownState();
      });

    // Control value changes
    this.selectControl?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((selections: any) => {
      this.selectedOptions = this.onSelectSetValue(selections);
    });
  }

  checkIfIdExists(idToCheck: number): boolean {
    return this.optionsSearched.some(item =>
      this.valueField && this.valueField !== ''
        ? item[this.valueField] === idToCheck
        : this.commonFunctionService.deepEqual(item, idToCheck)
    );
  }

  isObjectInData(objToCheck: any, data: any) {
    return data.some((dataObj: any) => JSON.stringify(dataObj) === JSON.stringify(objToCheck));
  }

  checkForTargetObject(array: any[], targetObject: any) {
    let isExit = false;
    for (const obj of array) {
      let obj1 = { ...obj };
      let obj2 = { ...targetObject };

      let obj3 = this.removeObjectKey(obj1, ['guestDisplayName', 'isAdded']);
      let obj4 = this.removeObjectKey(obj2, ['guestDisplayName', 'isAdded']);

      if (this.commonFunctionService.deepEqual(obj3, obj4)) {
        isExit = true;
        break; // Stop the iteration when the condition is met
      }
    }

    return isExit;
  }

  removeObjectKey(obj: any, keysToRemove: any) {
    // Create a shallow copy of the original object
    const newObj = { ...obj };

    // Remove the specified keys from the copy
    keysToRemove.forEach((key: any) => {
      if (newObj.hasOwnProperty(key)) {
        delete newObj[key];
      }
    });

    return newObj;
  }
}
